import { api } from "../../convex/_generated/api";
import { getSessionToken } from "./authService";
import { runConvexAction, runConvexMutation, runConvexQuery } from "./convexClient";

/**
 * Every request is authorized by the session token; the server resolves the
 * user from it. No client-supplied user ids.
 */
function withAuth(args = {}) {
  return { token: getSessionToken() ?? undefined, ...args };
}

export async function listProperties() {
  return runConvexQuery(api.properties.list, withAuth());
}

export async function createProperty(payload) {
  return runConvexMutation(api.properties.create, withAuth(payload));
}

export async function updateProperty(payload) {
  return runConvexMutation(api.properties.update, withAuth(payload));
}

export async function deleteProperty(id) {
  return runConvexMutation(api.properties.deleteProperty, withAuth({ id }));
}

export async function listTenants() {
  return runConvexQuery(api.tenants.list, withAuth());
}

export async function createTenant(payload) {
  return runConvexMutation(api.tenants.create, withAuth(payload));
}

export async function updateTenant(payload) {
  return runConvexMutation(api.tenants.update, withAuth(payload));
}

export async function listMaintenanceRequests() {
  return runConvexQuery(api.maintenanceRequests.list, withAuth());
}

export async function createMaintenanceRequest(payload) {
  return runConvexMutation(api.maintenanceRequests.create, withAuth(payload));
}

export async function updateMaintenanceRequest(payload) {
  return runConvexMutation(api.maintenanceRequests.update, withAuth(payload));
}

export async function updateMaintenanceStatus(payload) {
  return runConvexMutation(
    api.maintenanceRequests.updateStatus,
    withAuth(payload),
  );
}

export async function assignMaintenanceContractor(payload) {
  return runConvexMutation(
    api.maintenanceRequests.assignContractor,
    withAuth(payload),
  );
}

export async function listContractors(specialty) {
  return runConvexQuery(
    api.contractors.list,
    withAuth({ specialty: specialty || undefined }),
  );
}

export async function updateContractor(payload) {
  return runConvexMutation(api.contractors.update, withAuth(payload));
}

export async function createContractor(payload) {
  return runConvexMutation(api.contractors.create, withAuth(payload));
}

export async function listRentPayments() {
  return runConvexQuery(api.rentPayments.list, withAuth());
}

export async function createRentPayment(payload) {
  return runConvexMutation(api.rentPayments.create, withAuth(payload));
}

export async function updateRentPayment(payload) {
  return runConvexMutation(api.rentPayments.update, withAuth(payload));
}

export async function removeRentPayment(id) {
  return runConvexMutation(api.rentPayments.remove, withAuth({ id }));
}

export async function listExpenses() {
  return runConvexQuery(api.expenses.list, withAuth());
}

export async function createExpense(payload) {
  return runConvexMutation(api.expenses.create, withAuth(payload));
}

export async function updateExpense(payload) {
  return runConvexMutation(api.expenses.update, withAuth(payload));
}

export async function removeExpense(id) {
  return runConvexMutation(api.expenses.remove, withAuth({ id }));
}

export async function listUserProfile() {
  return runConvexQuery(api.users.current, withAuth());
}

export async function updateUserProfile(payload) {
  return runConvexMutation(api.users.update, withAuth(payload));
}

export async function listInsurancePolicies() {
  return runConvexQuery(api.insurancePolicies.list, withAuth());
}

export async function createInsurancePolicy(payload) {
  return runConvexMutation(api.insurancePolicies.create, withAuth(payload));
}

export async function updateInsurancePolicy(payload) {
  return runConvexMutation(api.insurancePolicies.update, withAuth(payload));
}

export async function removeInsurancePolicy(id) {
  return runConvexMutation(api.insurancePolicies.remove, withAuth({ id }));
}

export async function seedInsurancePolicies() {
  return runConvexMutation(api.insurancePolicies.seedDefault, withAuth());
}

export async function listRentalLicenses() {
  return runConvexQuery(api.rentalLicenses.list, withAuth());
}

export async function createRentalLicense(payload) {
  return runConvexMutation(api.rentalLicenses.create, withAuth(payload));
}

export async function updateRentalLicense(payload) {
  return runConvexMutation(api.rentalLicenses.update, withAuth(payload));
}

export async function removeRentalLicense(id) {
  return runConvexMutation(api.rentalLicenses.remove, withAuth({ id }));
}

export async function listVaultAccounts(section) {
  return runConvexQuery(
    api.vault.list,
    withAuth({ section: section || undefined }),
  );
}

export async function createVaultAccount(payload) {
  return runConvexMutation(api.vault.create, withAuth(payload));
}

export async function updateVaultAccount(payload) {
  return runConvexMutation(api.vault.update, withAuth(payload));
}

export async function removeVaultAccount(id) {
  return runConvexMutation(api.vault.remove, withAuth({ id }));
}

// Spreadsheet import (src/pages/Import.jsx). Each call writes only the rows
// the owner confirmed in the preview.
export async function importContractors({ creates = [], updates = [] }) {
  return runConvexMutation(api.workbookImport.importContractors, withAuth({ creates, updates }));
}

export async function importExpenses({ creates = [] }) {
  return runConvexMutation(api.workbookImport.importExpenses, withAuth({ creates }));
}

export async function importInsurancePolicies({ creates = [], updates = [] }) {
  return runConvexMutation(api.workbookImport.importInsurancePolicies, withAuth({ creates, updates }));
}

export async function importVaultAccounts({ creates = [], updates = [] }) {
  return runConvexMutation(api.workbookImport.importVaultAccounts, withAuth({ creates, updates }));
}

export async function importTenants({ creates = [], updates = [] }) {
  return runConvexMutation(api.workbookImport.importTenants, withAuth({ creates, updates }));
}

// OneDrive live sync (convex/oneDrive.ts).
export async function getOneDriveStatus() {
  return runConvexQuery(api.oneDrive.status, withAuth());
}

export async function startOneDriveConnect() {
  return runConvexAction(api.oneDrive.startConnect, withAuth({ returnTo: window.location.href }));
}

export async function searchOneDriveFiles(query) {
  return runConvexAction(api.oneDrive.searchFiles, withAuth({ query: query || undefined }));
}

export async function chooseOneDriveFile({ fileId, fileName, fileUrl }) {
  return runConvexMutation(api.oneDrive.chooseFile, withAuth({ fileId, fileName, fileUrl: fileUrl || undefined }));
}

export async function setOneDriveLiveSync(enabled) {
  return runConvexMutation(api.oneDrive.setLiveSync, withAuth({ enabled }));
}

export async function syncOneDriveNow() {
  return runConvexMutation(api.oneDrive.syncNow, withAuth());
}

export async function disconnectOneDrive() {
  return runConvexMutation(api.oneDrive.disconnect, withAuth());
}

export async function listNotes() {
  return runConvexQuery(api.notes.list, withAuth());
}

export async function createNote(payload) {
  return runConvexMutation(api.notes.create, withAuth(payload));
}

export async function updateNote(payload) {
  return runConvexMutation(api.notes.update, withAuth(payload));
}

export async function removeNote(id) {
  return runConvexMutation(api.notes.remove, withAuth({ id }));
}

export async function generateDocumentUploadUrl() {
  return runConvexMutation(api.documents.generateUploadUrl, withAuth());
}

export async function listDocuments({ propertyId, category, tag } = {}) {
  return runConvexQuery(
    api.documents.list,
    withAuth({
      propertyId: propertyId || undefined,
      category: category || undefined,
      tag: tag || undefined,
    }),
  );
}

export async function searchDocuments({ searchTerm, category } = {}) {
  return runConvexQuery(
    api.documents.search,
    withAuth({
      searchTerm,
      category: category || undefined,
    }),
  );
}

export async function getDocument(id) {
  return runConvexQuery(api.documents.get, withAuth({ id }));
}

export async function createDocument(payload) {
  return runConvexMutation(api.documents.create, withAuth(payload));
}

export async function updateDocument(payload) {
  return runConvexMutation(api.documents.update, withAuth(payload));
}

export async function uploadDocumentVersion(payload) {
  return runConvexMutation(api.documents.uploadNewVersion, withAuth(payload));
}

export async function removeDocument(id) {
  return runConvexMutation(api.documents.remove, withAuth({ id }));
}

export async function listRecurringTransactions() {
  return runConvexQuery(api.recurringTransactions.list, withAuth());
}

export async function createRecurringTransaction(payload) {
  return runConvexMutation(api.recurringTransactions.create, withAuth(payload));
}

export async function updateRecurringTransaction(payload) {
  return runConvexMutation(api.recurringTransactions.update, withAuth(payload));
}

export async function removeRecurringTransaction(id) {
  return runConvexMutation(api.recurringTransactions.remove, withAuth({ id }));
}

export async function listUpcomingReminders(horizonDays) {
  return runConvexQuery(
    api.reminders.upcoming,
    withAuth({ horizonDays: horizonDays || undefined }),
  );
}

export async function listReminders() {
  return runConvexQuery(api.reminders.list, withAuth({ includeCompleted: true }));
}

export async function createReminder(payload) {
  return runConvexMutation(api.reminders.create, withAuth(payload));
}

export async function updateReminder(payload) {
  return runConvexMutation(api.reminders.update, withAuth(payload));
}

export async function removeReminder(id) {
  return runConvexMutation(api.reminders.remove, withAuth({ id }));
}

export async function fetchCashflowProjection(horizonDays) {
  return runConvexQuery(
    api.cashflow.projection,
    withAuth({ horizonDays: horizonDays || undefined }),
  );
}

export async function fetchProfitAndLoss({
  startDate,
  endDate,
  propertyId,
} = {}) {
  return runConvexQuery(
    api.reports.profitAndLoss,
    withAuth({
      startDate,
      endDate,
      propertyId: propertyId || undefined,
    }),
  );
}

export async function fetchDashboardSnapshot() {
  const unavailableModules = [];
  const safeList = async (name, loader, fallback = []) => {
    try {
      return await loader();
    } catch (error) {
      // Keep dashboard usable if optional modules are not deployed yet.
      unavailableModules.push(name);
      console.warn(
        "Optional dashboard data unavailable:",
        error?.message || error,
      );
      return fallback;
    }
  };

  const [
    properties,
    tenants,
    maintenanceRequests,
    rentPayments,
    expenses,
    contractors,
    documents,
    insurancePolicies,
    rentalLicenses,
    upcomingReminders,
  ] = await Promise.all([
    listProperties(),
    listTenants(),
    listMaintenanceRequests(),
    listRentPayments(),
    listExpenses(),
    listContractors(),
    safeList("Documents", () => listDocuments(), []),
    safeList("Insurance", () => listInsurancePolicies(), []),
    safeList("Rental licenses", () => listRentalLicenses(), []),
    safeList("Reminders", () => listUpcomingReminders(90), []),
  ]);

  return {
    properties,
    tenants,
    maintenanceRequests,
    rentPayments,
    expenses,
    contractors,
    documents,
    insurancePolicies,
    rentalLicenses,
    upcomingReminders,
    unavailableModules,
  };
}
